import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CallOutlined,
  EmailOutlined,
  EventOutlined,
  NoteOutlined,
  TaskOutlined,
  AddRounded,
  PersonOutlined,
  OpenInFullRounded,
  CloseRounded,
} from '@mui/icons-material';
import {
  collection,
  onSnapshot,
  query,
  where,
  getDoc,
  doc,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { RegisterActivityDialog } from './RegisterActivityDialog';
import { formatDateTime } from '../../../lib/i18n-format';

interface CrmTimelineProps {
  personId: string;
}

// ─── Labels en español ───────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  call: 'Llamada',
  email: 'Correo',
  meeting: 'Reunión',
  note: 'Nota',
  task: 'Tarea',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  call: <CallOutlined fontSize="small" color="primary" />,
  email: <EmailOutlined fontSize="small" color="info" />,
  meeting: <EventOutlined fontSize="small" color="secondary" />,
  note: <NoteOutlined fontSize="small" color="warning" />,
  task: <TaskOutlined fontSize="small" color="success" />,
};

// ─── Cache local UID → displayName ──────────────────────────────────────────
const nameCache: Record<string, string> = {};

// ─── Email Preview Dialog ────────────────────────────────────────────────────

interface EmailPreviewDialogProps {
  open: boolean;
  activity: DocumentData | null;
  actorName: string;
  onClose: () => void;
}

function EmailPreviewDialog({ open, activity, actorName, onClose }: EmailPreviewDialogProps) {
  // Build a full HTML document to inject via srcdoc (timing-safe, no DOM mutation needed)
  const emailSrcdoc = activity?.emailBody
    ? `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:0;background:#ffffff;font-family:sans-serif;}</style></head><body>${activity.emailBody}</body></html>`
    : '';

  if (!activity) return null;

  const typeLabel = TYPE_LABELS[activity.type] ?? activity.type;
  const dateStr = activity.occurredAt
    ? formatDateTime(activity.occurredAt, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Chip
                label={typeLabel}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
              />
              <Typography variant="subtitle1" fontWeight={600}>
                {activity.subject ?? '(Sin asunto)'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PersonOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                Registrado por <strong>{actorName}</strong> · {dateStr}
              </Typography>
            </Stack>
          </Box>
          <Tooltip title="Cerrar">
            <IconButton size="small" onClick={onClose}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        {/* Notas de la actividad */}
        {activity.notes && (
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {activity.notes}
            </Typography>
          </Box>
        )}

        {/* Contenido del correo */}
        {activity.emailBody ? (
          <iframe
            title="Vista previa del correo"
            srcDoc={emailSrcdoc}
            style={{
              width: '100%',
              height: '520px',
              border: 'none',
              display: 'block',
              backgroundColor: '#ffffff',
            }}
            sandbox="allow-same-origin allow-scripts"
          />
        ) : (
          <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              No hay vista previa disponible para esta actividad.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} size="small">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export function CrmTimeline({ personId }: CrmTimelineProps) {
  const { currentTenantId } = useTenantStore();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<DocumentData[]>([]);
  const [actorNames, setActorNames] = useState<Record<string, string>>({});
  const [previewActivity, setPreviewActivity] = useState<DocumentData | null>(null);
  const [openRegister, setOpenRegister] = useState(false);

  // Resuelve el displayName de un UID consultando el membership
  const resolveActorName = useCallback(
    async (uid: string): Promise<string> => {
      if (!uid || !currentTenantId) return uid;
      if (nameCache[uid]) return nameCache[uid];

      try {
        const snap = await getDoc(doc(db, `tenants/${currentTenantId}/memberships/${uid}`));
        const name: string = snap.exists()
          ? (snap.data()?.displayName ?? snap.data()?.email ?? uid)
          : uid;
        nameCache[uid] = name;
        return name;
      } catch {
        return uid;
      }
    },
    [currentTenantId],
  );

  useEffect(() => {
    if (!currentTenantId || !personId) return;
    setLoading(true);

    const q = query(
      collection(db, `tenants/${currentTenantId}/crmActivities`),
      where('linkedPersonId', '==', personId),
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Sort desc client-side
      docs.sort((a: any, b: any) => {
        const aTime = a.occurredAt?.toDate?.()?.getTime() ?? 0;
        const bTime = b.occurredAt?.toDate?.()?.getTime() ?? 0;
        return bTime - aTime;
      });

      setActivities(docs);
      setLoading(false);

      // Resolver nombres únicos de actores
      const uniqueUids = [...new Set(docs.map((d: any) => d.createdBy).filter(Boolean))];
      const resolved: Record<string, string> = {};
      await Promise.all(
        uniqueUids.map(async (uid) => {
          resolved[uid as string] = await resolveActorName(uid as string);
        }),
      );
      setActorNames((prev) => ({ ...prev, ...resolved }));
    });

    return () => unsubscribe();
  }, [currentTenantId, personId, resolveActorName]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Bitácora de Interacciones
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          size="small"
          sx={{ borderRadius: 2 }}
          onClick={() => setOpenRegister(true)}
        >
          Registrar Actividad
        </Button>
      </Stack>

      {activities.length === 0 ? (
        <Card
          variant="outlined"
          sx={{ borderRadius: 3, textAlign: 'center', py: 6, bgcolor: 'background.default' }}
        >
          <Typography variant="body1" color="text.secondary">
            No hay actividades registradas en la bitácora aún.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {activities.map((act) => {
            const typeLabel = TYPE_LABELS[act.type] ?? act.type;
            const icon = TYPE_ICONS[act.type] ?? <NoteOutlined fontSize="small" />;
            const actor = actorNames[act.createdBy] ?? act.createdBy ?? '—';
            const hasEmailPreview = act.type === 'email' && !!act.emailBody;
            const dateStr = act.occurredAt
              ? formatDateTime(act.occurredAt, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—';

            return (
              <Card
                key={act.id}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  cursor: hasEmailPreview ? 'pointer' : 'default',
                  transition: 'box-shadow 0.15s',
                  '&:hover': hasEmailPreview ? { boxShadow: 3 } : undefined,
                }}
                onClick={hasEmailPreview ? () => setPreviewActivity(act) : undefined}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    {/* Ícono de tipo */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {icon}
                    </Box>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {/* Cabecera: tipo + fecha + acción ver correo */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ sm: 'center' }}
                        mb={0.5}
                        gap={0.5}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            label={typeLabel}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                          />
                          {act.subject && (
                            <Typography variant="subtitle2" fontWeight={600} noWrap>
                              {act.subject}
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {hasEmailPreview && (
                            <Tooltip title="Ver correo completo">
                              <Chip
                                icon={<OpenInFullRounded sx={{ fontSize: '14px !important' }} />}
                                label="Ver correo"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20, cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewActivity(act);
                                }}
                              />
                            </Tooltip>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {dateStr}
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Notas */}
                      {act.notes && (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                        >
                          {act.notes}
                        </Typography>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      {/* Pie: registrado por */}
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PersonOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">
                          Registrado por <strong>{actor}</strong>
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Dialog de preview de correo */}
      <EmailPreviewDialog
        open={!!previewActivity}
        activity={previewActivity}
        actorName={
          previewActivity
            ? (actorNames[previewActivity.createdBy] ?? previewActivity.createdBy ?? '—')
            : '—'
        }
        onClose={() => setPreviewActivity(null)}
      />

      {/* Dialog de registro manual */}
      <RegisterActivityDialog
        open={openRegister}
        personId={personId}
        onClose={() => setOpenRegister(false)}
      />
    </Box>
  );
}
